import { Device } from '@twilio/voice-sdk';
import { AppIncomingCall } from '@/lib/callProviders/types';
import { useCaptureClientCall } from '@/lib/callProviders/use-capture-client-call';
import { getProvider } from '@/lib/callProviders/provider-registry';
import { useDeviceEvents } from '@/lib/callProviders/twilio/use-device-events';
import { useCallEvents } from '@/lib/callProviders/twilio/use-call-events';
import { usePhone } from '@modules/phone/hooks/use-phone';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { useDialerStore } from '@modules/phone/store/dialer.store';
import { useDialerCallPulseStore } from '@modules/phone/store/dialer-call-pulse.store';
import { useWaitingCallStore } from '@modules/phone/store/waiting-call.store';
import { TwilioCall } from '@/types/app.types';
import { useSwitchCallStore as switchCallStore } from '@shared/layouts/app-layout/components/app-banner/banners/store/use-switch-call.store';

export function useCallProviders() {
  const { captureCallInfo } = useCaptureClientCall();
  const { setCallState, updateAcceptedTimestamp } = useDeviceStore();
  const { setIsIncoming, openIncomingPhone, openPhoneInCallMode, setPopupView } = usePhoneStore();
  const { closePhone } = usePhone();
  const { openCalls, closeKeypad } = useDialerStore();

  function handleIncomingCall(incomingCall: AppIncomingCall) {
    const provider = getProvider(incomingCall.provider);
    if (!provider) return;

    const { callId, memberId, type, onAnotherCall, isAlreadyRinging } = incomingCall;
    captureCallInfo(callId);

    if (type === 'live' || type === 'switching') {
      provider.accept(incomingCall);
      setCallState(incomingCall);
      return;
    }

    /**@description When already on a call, capture the second call as waitingCall instead of
     * rejecting it outright — a third simultaneous call is still rejected. */
    if (onAnotherCall) {
      if (useWaitingCallStore.getState().waitingCall) {
        provider.reject(incomingCall);
        return;
      }

      useWaitingCallStore.getState().setWaitingCall(incomingCall.providerCall as TwilioCall);
      setPopupView(false);
      return;
    }

    /**@description Reject when call is already RINGING */
    if (isAlreadyRinging) return provider.reject(incomingCall);

    setCallState(incomingCall);
    if (memberId) {
      openIncomingPhone(true, memberId);
    } else {
      openIncomingPhone();
    }
    setIsIncoming(true);
  }

  function handleCallAccepted(call: AppIncomingCall) {
    captureCallInfo(call.callId);

    // prevent opening dialer on live call
    if (call.type === 'live') return;

    openPhoneInCallMode();
    updateAcceptedTimestamp(new Date().toISOString());
    openCalls();
    closeKeypad();
  }

  function handleCallDisconnected(call: AppIncomingCall) {
    captureCallInfo(call.callId);

    const switchStore = switchCallStore.getState();
    if (switchStore.showSwitchCall) {
      switchCallStore.setState({ showSwitchCall: false });
    }

    const { waitingCall, resetWaitingCall } = useWaitingCallStore.getState();

    if (waitingCall) {
      const memberId = waitingCall.customParameters.get('memberId');

      setCallState({ provider: 'twilio', providerCall: waitingCall });
      resetWaitingCall();
      // the promoted call starts from a clean pulse state — recording timestamps, call
      // status, transfer info etc. all belonged to the call that just ended.
      useDialerCallPulseStore.getState().resetDialerCallPulse();
      (useDeviceStore.getState().device?.providerDevice as Device | undefined)?.audio?.incoming(true);

      if (memberId) {
        openIncomingPhone(true, memberId);
      } else {
        openIncomingPhone();
      }
      setIsIncoming(true);
      return;
    }

    setCallState(null);
    closePhone();
  }

  // twilio for now; register additional providers under lib/callProviders and dispatch here
  useDeviceEvents(handleIncomingCall);
  useCallEvents(handleCallAccepted, handleCallDisconnected);

  return {
    handleIncomingCall,
  };
}
